import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/shared/services/query-keys';
import { useUIStore } from '@/shared/stores/ui-store';
import { getApiErrorMessage } from '@/shared/utils/api-error-message';
import type { LaunchOutcome, MetaPlacement } from '../types/checkout.types';
import {
  authorizeCampaignPayment,
  cancelCampaignPayment,
  captureCampaignPayment,
} from './checkout-api';

export function useAuthorizeCampaignPayment(campaignId: string) {
  const queryClient = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: (placements: MetaPlacement[]) => authorizeCampaignPayment(campaignId, placements),
    retry: 0,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.payment(campaignId) });
    },
    onError: (error) => {
      addToast({ message: getApiErrorMessage(error, 'Failed to start payment'), variant: 'error' });
    },
  });
}

export function useCaptureCampaignPayment(campaignId: string) {
  const queryClient = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: (outcome: LaunchOutcome) => captureCampaignPayment(campaignId, outcome),
    retry: 0,
    onSuccess: (payment) => {
      queryClient.setQueryData(queryKeys.campaigns.payment(campaignId), payment);
      addToast({ message: 'Payment captured', variant: 'success' });
    },
    onError: (error) => {
      addToast({
        message: getApiErrorMessage(error, 'Failed to capture payment'),
        variant: 'error',
      });
    },
  });
}

export function useCancelCampaignPayment(campaignId: string) {
  const queryClient = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: () => cancelCampaignPayment(campaignId),
    retry: 0,
    onSuccess: (payment) => {
      queryClient.setQueryData(queryKeys.campaigns.payment(campaignId), payment);
      addToast({ message: 'Hold released, nothing was charged', variant: 'success' });
    },
    onError: (error) => {
      addToast({ message: getApiErrorMessage(error, 'Failed to release hold'), variant: 'error' });
    },
  });
}
